/**
 * No New Units Modal
 * 
 * Displayed when user clicks "Add Units" but there are no unmapped units
 * in any connected channel.
 */

import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal, Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { T_KEYS } from '../../constants';

// Styled Components
const ModalWrapper = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '480px',
  backgroundColor: '#fff',
  borderRadius: '20px',
  boxShadow: '2px 4px 20px 0px rgba(0, 0, 0, 0.1)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
});

const IconContainer = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  backgroundColor: '#FFF4D6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  textAlign: 'center',
  width: '100%',
});

const Title = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '28px',
  color: '#262626',
});

const Description = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#666666',
});

const ButtonsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

const PrimaryButton = styled(Button)({
  backgroundColor: '#5B3FFF',
  color: '#fff',
  borderRadius: '8px',
  padding: '16px',
  fontFamily: '"Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#4A32CC',
  },
});

const SecondaryButton = styled(Button)({
  backgroundColor: '#F2F2F2',
  color: '#262626',
  borderRadius: '8px',
  padding: '16px',
  fontFamily: '"Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#E5E5E5',
  },
});

export interface NoNewUnitsModalProps {
  open: boolean;
  onClose: () => void;
}

const NoNewUnitsModal: FC<NoNewUnitsModalProps> = memo(({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleGoToChannels = () => {
    onClose();
    navigate('/channels');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="no-new-units-modal"
    >
      <ModalWrapper>
        <IconContainer>
          <WarningAmberRoundedIcon sx={{ fontSize: 28, color: '#F5A623' }} />
        </IconContainer>

        <MessageContainer>
          <Title>
            {t(T_KEYS.NO_NEW_UNITS_TITLE)}
          </Title>
          <Description>
            {t(T_KEYS.NO_NEW_UNITS_DESC)}
          </Description>
          <Description>
            {t(T_KEYS.NO_NEW_UNITS_HINT)}
          </Description>
        </MessageContainer>

        <ButtonsContainer>
          <PrimaryButton onClick={handleGoToChannels} fullWidth>
            {t(T_KEYS.GO_TO_CHANNELS)}
          </PrimaryButton>
          <SecondaryButton onClick={onClose} fullWidth>
            {t(T_KEYS.CLOSE)}
          </SecondaryButton>
        </ButtonsContainer>
      </ModalWrapper>
    </Modal>
  );
});

NoNewUnitsModal.displayName = 'NoNewUnitsModal';

export default NoNewUnitsModal;
